/* eslint-disable no-plusplus,max-len */
/* global G */
import { curry } from 'lib/util'
import { apply as applyAttachments } from 'app/_shared/events/attachment'

/**
 * Tracks the state of the currently being uploaded attachments.
 *
 * @param {Gaia.AppModule.Spec} module              the current module composition object
 * @param {Gaia.Component.Spec} component           the current action's main component
 * @param {Gaia.Adapter.Attachment[]} [attachments] optional attachments for the message
 * @param {Gaia.PlatformEvent} event                the event that triggered the upload
 * @param {string} event.detail.key                 the message's client-generated key
 */
export const trackAttachments = curry((module, component, attachments, event) => {
  const eventBus = module[G.ADAPTER][G.EVENTS]

  if (!attachments || !attachments.length) return

  const parent = event?.detail?.key

  // All attachments uploaded
  const onDone = ({ detail }) => {
    eventBus.remove(eventBus.type(G.ATTACHMENT, G.DONE, parent), onDone)
    const messageAdapter = module[G.ADAPTER][G.MESSAGE]
    const item = messageAdapter[G.DATA].find(m => m.key === parent)
    if (item) item.value.attachments = detail?.[G.DATA] || []
    eventBus.dispatch(eventBus.type(G.MESSAGE, G.DONE), { [G.DATA]: messageAdapter[G.DATA] })
  }
  eventBus.add(eventBus.type(G.ATTACHMENT, G.DONE, parent), onDone)

  // Hand the batch over to the attachment adapter, which registers and tracks the in-flight uploads.
  eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.UPLOAD), {
    [G.DATA]: { parent, attachments },
  })
})

/**
 * Starts uploading all local `attachments` of the message attachment group in the background,
 *
 * The uploads are handed over to the attachment adapter, which keeps track of them (and their
 * progress) in its own `G.STATE.uploads`.
 *
 * @param {Gaia.AppModule.Spec} module              the current module composition object
 * @param {Gaia.Component.Spec} component           the current action's main component
 * @param {Gaia.Adapter.Attachment[]} [attachments] optional attachments for the message
 * @param {Gaia.PlatformEvent} event                the event that triggered the upload
 * @param {string} event.detail.key                 the message's client-generated key
 */
const uploadAttachments = (module, component, attachments, event) => {
  const eventBus = module[G.ADAPTER][G.EVENTS]

  if (!attachments || !attachments.length) return

  const parent = event?.detail?.key

  // Start the actual uploads (workers)
  applyAttachments(module, component, {
    ...event,
    detail: { ...event?.detail, api: 'message', ref: parent },
  })

  // clear message attachments so user can add new ones
  for (let i = 0; i < attachments.length; i++) {
    eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.REMOVE, 'message'), { [G.DATA]: attachments[i] })
  }
}

export default curry(uploadAttachments)
